import MaterialTable, { MTableToolbar } from '@material-table/core';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import SyncAltSharpIcon from '@mui/icons-material/SyncAltSharp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Autocomplete, Box, FormControl, IconButton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import { CATEGORY_OPTIONS, CATEGORY_VIEW_MAPPING } from 'common/Constant';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { hasPermission } from 'utils/common';
import { viewReport, fetchReportTags } from '../actions/actions';
import * as Actions from './actions/actions';
import EditDialog from './editDialog';
import InitOptionDialog from './initOptionDialog';

const useStyles = makeStyles(() => ({
  container: {
    padding: '2rem 0'
  },
  categoryDropdown: {
    paddingLeft: '2rem',
    minWidth: '200px',
    '& .MuiInputBase-root': {
      backgroundColor: '#ffffff'
    },
    '& .MuiAutocomplete-inputRoot': {
      backgroundColor: '#ffffff'
    }
  }
}));

const ExistingReportTable = () => {
  const tableRef = useRef();
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const tenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const defaultUserState = useSelector((state) => state.user.defaultUserState);

  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [pageSize, setPageSize] = useState(Constant.DEFAULT_SELECT_PAGE_SIZE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(undefined);
  const [initDialogOpen, setInitDialogOpen] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    store.dispatch(fetchReportTags());
  }, []);

  const handleDelete = (rowData) => {
    confirmDialog(
      () => {
        axios
          .delete(AppConfig.FO_INITIAL_URL + '/report/' + rowData.id)
          .then((_res) => refresh())
          .catch((err) => {
            store.dispatch({
              type: AppActionTypes.APPSTATE_SET_APP_ERROR,
              payload: `Could not get save report: ${err}`
            });
          });
      },
      'Deleting this report will also delete any associated reports, such as favorites. Are you sure you want to continue?',
      'Delete Report'
    );
  };

  const handleThunbmailSyncup = () => {
    confirmDialog(
      () => {
        store.dispatch(Actions.syncThunbnail(tenantCd));
      },
      'Do you want to sync up all thumbnails for current tenant?',
      'Sync Thumbnail'
    );
  };

  const handleInitReport = () => {
    setInitDialogOpen(true);
  };

  const handleDialogClose = (data) => {
    if (data) {
      axios
        .post(AppConfig.FO_INITIAL_URL + '/report', data)
        .then((_res) => {
          refresh();
        })
        .catch((err) => {
          store.dispatch({
            type: AppActionTypes.APPSTATE_SET_APP_ERROR,
            payload: `Could not save report: ${err}`
          });
        });
    }
    setDialogOpen(false);
    setEditTarget(undefined);
  };

  const handleInitDialogClose = (payload) => {
    if (payload) {
      store.dispatch(Actions.initReportForTenant(tenantCd, payload.mode));
    }

    setInitDialogOpen(false);
  };
  const refresh = () => {
    tableRef.current.onQueryChange();
  };

  const getCurrentCategory = () => {
    if (category?.value === 'ALL') {
      return undefined;
    } else {
      // Free typed entries will not have value property
      return category?.value ?? category;
    }
  };

  useEffect(() => {
    refresh();
  }, [tenantCd]);

  useEffect(() => {
    if (
      !defaultUserState &&
      !hasPermission(AppConfig.PERMISSION.FO_REPORT_MANAGEMENT, permissions)
    ) {
      navigate('/fo');
    }
  }, [defaultUserState, permissions]);

  const renderPreview = (view) => {
    return (
      <IconButton
        className="p-1"
        onClick={(e) => {
          e.stopPropagation();
          store.dispatch(viewReport(view, navigate, location));
        }}
        size="large">
        <VisibilityIcon />
      </IconButton>
    );
  };

  return (
    <>
      <Box className={styles.container}>
        <MaterialTable
          title="Existing Reports"
          tableRef={tableRef}
          columns={[
            { title: 'Display Text', field: 'displayText', width: '35%' },
            {
              title: 'Category',
              field: 'category',
              render: (data) =>
                CATEGORY_VIEW_MAPPING[data.category] || 'UNKNOWN'
            },
            { title: 'Description', field: 'description' },
            { title: 'Order', field: 'order' },
            {
              title: 'Preview',
              field: 'reptSource',
              render: (rowData) => {
                return renderPreview(rowData);
              }
            },
            {
              title: 'Last Updated',
              field: 'updateDt',
              render: (rowData) => {
                return moment(rowData.updateDt).format('MMM Do YY, h:mm:ss a');
              }
            }
          ]}
          onChangeRowsPerPage={setPageSize}
          data={(query) =>
            new Promise((resolve, reject) => {
              axios
                .post(AppConfig.FO_INITIAL_URL + '/report/search', {
                  page: query.page,
                  size: query.pageSize,
                  direction: query.orderDirection
                    ? query.orderDirection.toUpperCase()
                    : Constant.DIRECTION_ASC,
                  category: getCurrentCategory(),
                  searchTerm: query.search,
                  sortProperty: query.orderBy
                    ? query.orderBy.field
                    : 'displayText'
                })
                .then((res) => {
                  const data = res.data;
                  setData(data?.content);
                  resolve({
                    data: data.content,
                    page: data.number,
                    size: data.size,
                    totalCount: data.totalElements
                  });
                })
                .catch((err) => {
                  resolve({
                    data: [],
                    page: 0,
                    totalCount: 0
                  });
                  if (!axios.isCancel(err)) {
                    if (err?.response.status === 400) {
                      store.dispatch({
                        type: AppActionTypes.APPSTATE_SET_APP_ERROR,
                        payload: `Could not find reports for subcategory: ${category}`
                      });
                    } else {
                      store.dispatch({
                        type: AppActionTypes.APPSTATE_SET_APP_ERROR,
                        payload: `Could not get report info: ${err}`
                      });
                    }
                  }
                });
            })
          }
          options={{
            actionsColumnIndex: -1,
            search: true,
            paging: true,
            debounceInterval: 1500,
            pageSizeOptions: Constant.DEFAULT_SEARCH_PAGE_SIZE,
            pageSize: pageSize
          }}
          actions={[
            {
              position: 'row',
              icon: () => <EditIcon />,
              tooltip: 'Edit',
              onClick: (_e, rowData) => {
                setDialogOpen(true);
                setEditTarget(rowData);
              }
            },
            (data) => ({
              icon: () => <DeleteIcon />,
              tooltip: 'Delete',
              onClick: (_e, rowData) => handleDelete(rowData)
            }),
            {
              icon: () => <RefreshIcon />,
              tooltip: 'Refresh Data',
              onClick: () => {
                refresh();
              },
              isFreeAction: true
            },
            {
              icon: () => <SyncAltSharpIcon />,
              tooltip: 'Init. Report',
              isFreeAction: true,
              onClick: (event) => handleInitReport()
            },
            {
              icon: () => <SyncIcon />,
              tooltip: 'Sync thumbnails',
              isFreeAction: true,
              onClick: (event) => handleThunbmailSyncup()
            }
          ]}
          components={{
            OverlayLoading: () => <div />,
            Toolbar: (props) => (
              <Box className={styles.categoryDropdown}>
                <MTableToolbar {...props} />
                <FormControl size="medium">
                  <Autocomplete
                    className={styles.categoryDropdown}
                    id="input_manageReport"
                    key="input_manageReport"
                    freeSolo
                    includeInputInList
                    options={CATEGORY_OPTIONS}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={category ?? ''}
                    onChange={(_event, newValue) => {
                      setCategory(newValue);
                      refresh();
                    }}
                    slotProps={{
                      listbox: {
                        sx: {
                          '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ':
                            {
                              color: '#000000 !important'
                            },
                          '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: '#5470b8 !important'
                          },
                          '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                            backgroundColor: '#f4f5f6 !important'
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" />
                    )}
                  />
                </FormControl>
              </Box>
            )
          }}
          icons={{
            Search: () => <SearchIcon color="primary"/>,
          }}
        />
        <EditDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          target={editTarget}
        />
        <InitOptionDialog
          open={initDialogOpen}
          onClose={handleInitDialogClose}
          target={data}
        />
      </Box>
    </>
  );
};

export default ExistingReportTable;
